/**
 * School self-registration plan amounts for UI.
 * Keep in sync with argus-backend/functions/src/utils/schoolRegistrationPlans.ts
 * and INSTITUTIONAL_PLAN_ASSESSMENT_GATE_MIN_LEVEL (backend) for assessment floors.
 */

/** Institutional school packages are charged at package price only; no tax is added separately. */
export const SCHOOL_REGISTRATION_GST_RATE = 0 as const;

export const REGISTER_PLAN_IDS = ['entry', 'standard', 'premium'] as const;
export type RegisterPlanId = (typeof REGISTER_PLAN_IDS)[number];

/** Production annual package fee (INR) - same as REGISTER_PLAN_META.price_inr on the API */
const PRODUCTION_INR: Record<RegisterPlanId, number> = {
  entry: 200_000,
  standard: 300_000,
  premium: 500_000,
};

export const SCHOOL_INSTITUTIONAL_BASE_INR: Readonly<Record<RegisterPlanId, number>> = PRODUCTION_INR;

function formatInr(amount: number): string {
  return `₹${amount.toLocaleString('en-IN')}`;
}

function formatInrFromPaise(paise: number): string {
  const rupees = paise / 100;
  return `₹${rupees.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

export type SchoolRegistrationTotalsPaise = {
  basePaise: number;
  gstPaise: number;
  totalPaise: number;
};

/** Matches backend `getSchoolRegistrationAmountPaise` (package INR × 100, no tax added). */
export function schoolRegistrationTotalsFromBaseInr(baseInr: number): SchoolRegistrationTotalsPaise {
  const basePaise = Math.round(baseInr * 100);
  const totalPaise = basePaise;
  const gstPaise = 0;
  return { basePaise, gstPaise, totalPaise };
}

export function schoolRegistrationCheckoutSummaryFromBaseInr(baseInr: number): {
  baseDisplay: string;
  gstDisplay: string;
  totalDisplay: string;
  gstRatePct: number;
} {
  const { gstPaise, totalPaise } = schoolRegistrationTotalsFromBaseInr(baseInr);
  return {
    baseDisplay: formatInr(baseInr),
    gstDisplay: formatInrFromPaise(gstPaise),
    totalDisplay: formatInrFromPaise(totalPaise),
    gstRatePct: Math.round(SCHOOL_REGISTRATION_GST_RATE * 100),
  };
}

export type SchoolPlanDisplay = {
  id: RegisterPlanId;
  name: string;
  price: string;
  /** INR for display parity with API; not paise */
  priceNum: number;
  period: string;
  tagline: string;
  features: string[];
  popular: boolean;
};

const PLAN_COPY: Pick<SchoolPlanDisplay, 'id' | 'name' | 'tagline' | 'features' | 'popular'>[] = [
  {
    id: 'entry',
    name: 'Entry',
    tagline: 'Core benchmarking for one assessment',
    features: [
      'Assessment 1 (Analytical Reasoning)',
      'Headline performance report',
      'Tier distribution analysis',
      'Path to next tier',
    ],
    popular: false,
  },
  {
    id: 'standard',
    name: 'Standard',
    tagline: 'Full reasoning triad & deep analytics',
    features: [
      'Assessments 1–3 (full reasoning triad)',
      'Full analytics & subscore breakdowns',
      'Class-level analysis',
      'Comparative benchmarks (national, regional)',
      'Quarterly growth tracking',
      'Prioritised recommendations',
    ],
    popular: true,
  },
  {
    id: 'premium',
    name: 'Premium',
    tagline: 'Standard plus Exams 4–5 (Personality & AI) and consulting',
    features: [
      'Everything in Standard',
      'Assessments 4–5 (Personality and Interest & AI Proficiency)',

      'Cohort analysis & cluster insights',
      'Consulting-style action plans',
      'Dedicated account manager',
      'Marketing toolkit (tier badges, parent comms)',
    ],
    popular: false,
  },
];

function buildPlans(): SchoolPlanDisplay[] {
  return PLAN_COPY.map((row) => {
    const n = PRODUCTION_INR[row.id];
    return {
      ...row,
      price: formatInr(n),
      priceNum: n,
      period: '/yr',
    };
  });
}

/** Plan cards for the school registration flow (package prices; no tax is added separately). */
export const SCHOOL_REGISTRATION_PLANS: SchoolPlanDisplay[] = buildPlans();

/** Institutional landing page - production annual fees only */
export const SCHOOL_INSTITUTIONAL_PRICE_LANDING: Record<RegisterPlanId, string> = {
  entry: `${formatInr(PRODUCTION_INR.entry)}/yr`,
  standard: `${formatInr(PRODUCTION_INR.standard)}/yr`,
  premium: `${formatInr(PRODUCTION_INR.premium)}/yr`,
};

/** Student roster guidance per tier (marketing; align with school FAQ). */
export const INSTITUTIONAL_PLAN_STUDENT_LIMIT: Record<RegisterPlanId, string> = {
  entry: 'Up to ~200 students',
  standard: 'Up to ~500 students',
  premium: 'No student cap (per campus)',
};

/** Hard roster cap by institutional package. `null` means no per-campus student cap. */
export const INSTITUTIONAL_PLAN_STUDENT_CAP: Record<RegisterPlanId, number | null> = {
  entry: 200,
  standard: 500,
  premium: null,
};

/** Sanity ceiling for platform-admin per-school `student_cap_override` (matches invite-list max). */
export const MAX_STUDENT_CAP_OVERRIDE = 5000;

/**
 * Manual platform-admin override stored on the school doc as `student_cap_override`.
 * Returns a non-negative integer when set; `null` when unset / invalid (fall back to plan).
 */
export function parseStudentCapOverride(raw: unknown): number | null {
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    const n = Math.floor(raw);
    if (n < 0 || n > MAX_STUDENT_CAP_OVERRIDE) return null;
    return n;
  }
  if (typeof raw === 'string' && raw.trim()) {
    const n = Number(raw.trim());
    if (!Number.isFinite(n)) return null;
    const floored = Math.floor(n);
    if (floored < 0 || floored > MAX_STUDENT_CAP_OVERRIDE) return null;
    return floored;
  }
  return null;
}

export function getSchoolPlanStudentCap(planId: unknown): number | null {
  const normalized = normalizeRegisterPlanId(planId);
  if (!normalized) return null;
  return INSTITUTIONAL_PLAN_STUDENT_CAP[normalized] ?? null;
}

/**
 * Effective roster cap for a school: manual override wins when present, else package default.
 * `null` = unlimited (e.g. premium with no override).
 */
export function getSchoolStudentCap(schoolData: {
  selected_plan_id?: unknown;
  student_cap_override?: unknown;
}): number | null {
  const override = parseStudentCapOverride(schoolData.student_cap_override);
  if (override !== null) return override;
  return getSchoolPlanStudentCap(schoolData.selected_plan_id);
}

/**
 * Max consumer membership tier included with each institutional school package.
 * Keep in sync with backend `INSTITUTIONAL_PLAN_ASSESSMENT_GATE_MIN_LEVEL`.
 */
export const INSTITUTIONAL_PLAN_COVERED_MEMBERSHIP_LEVEL: Record<RegisterPlanId, number> = {
  entry: 1,
  standard: 2,
  premium: 3,
};

export function normalizeRegisterPlanId(raw: unknown): RegisterPlanId | null {
  if (typeof raw !== 'string') return null;
  const normalized = raw.trim().toLowerCase();
  return REGISTER_PLAN_IDS.includes(normalized as RegisterPlanId) ? (normalized as RegisterPlanId) : null;
}

/** Resolve plan id from Firestore `selected_plan_id` and/or display name `subscription_plan`. */
export function resolveRegisterPlanIdFromFields(
  selectedPlanId: unknown,
  subscriptionPlan?: unknown
): RegisterPlanId | null {
  const fromId = normalizeRegisterPlanId(selectedPlanId);
  if (fromId) return fromId;

  if (typeof subscriptionPlan !== 'string') return null;
  const needle = subscriptionPlan.trim().toLowerCase();
  if (!needle) return null;

  for (const id of REGISTER_PLAN_IDS) {
    if (needle === id) return id;
    const row = PLAN_COPY.find((p) => p.id === id);
    if (row && row.name.toLowerCase() === needle) return id;
  }
  if (needle.includes('entry')) return 'entry';
  if (needle.includes('standard')) return 'standard';
  if (needle.includes('premium')) return 'premium';
  return null;
}

/** Annual package fee in INR  -  prefers API value, falls back to plan catalog. */
export function resolveSchoolPlanPriceInr(params: {
  plan_price_inr?: number | null;
  selected_plan_id?: string | null;
  subscription_plan?: string | null;
}): number | null {
  if (
    typeof params.plan_price_inr === 'number' &&
    Number.isFinite(params.plan_price_inr) &&
    params.plan_price_inr > 0
  ) {
    return params.plan_price_inr;
  }
  const planId = resolveRegisterPlanIdFromFields(params.selected_plan_id, params.subscription_plan);
  return planId ? SCHOOL_INSTITUTIONAL_BASE_INR[planId] : null;
}

/** Locale-formatted INR digits for amount inputs (commas stripped before submit). */
export function formatPlanAmountInrInput(amount: number | null | undefined): string {
  if (typeof amount !== 'number' || !Number.isFinite(amount) || amount <= 0) return '';
  return amount.toLocaleString('en-IN');
}

/** Production annual fee as card headline only (no "/yr") - same INR as {@link PRODUCTION_INR}. */
export function institutionalAnnualPriceRupeesOnly(planId: RegisterPlanId): string {
  return formatInr(PRODUCTION_INR[planId]);
}

export type InstitutionalPlanMatrixRow = {
  id: RegisterPlanId;
  name: string;
  tagline: string;
  /** Feature bullets - same source as school registration / admin plan cards */
  features: readonly string[];
  annualPriceRupeeDisplay: string;
  rosterGuidance: string;
  popular: boolean;
};

/**
 * Single source for institutional pricing cards, FAQ, and admin "Available plans".
 * Prices use production list amounts.
 */
export const SCHOOL_INSTITUTIONAL_PLAN_MATRIX: readonly InstitutionalPlanMatrixRow[] =
  REGISTER_PLAN_IDS.map((id) => {
    const row = PLAN_COPY.find((p) => p.id === id)!;
    return {
      id,
      name: row.name,
      tagline: row.tagline,
      features: row.features as readonly string[],
      annualPriceRupeeDisplay: institutionalAnnualPriceRupeesOnly(id),
      rosterGuidance: INSTITUTIONAL_PLAN_STUDENT_LIMIT[id],
      popular: row.popular,
    };
  });

/** Short paragraphs on the For Schools "Plans & pricing" strip - aligned with {@link PLAN_COPY}. */
export const INSTITUTIONAL_LANDING_PLAN_CARD_BLURBS: Record<RegisterPlanId, string> = {
  entry:
    'Core benchmarking with Assessment 1, headline reports, tier distribution, and path to the next tier.',
  standard:
    'Full reasoning triad (Assessments 1–3), deep analytics, benchmarks, quarterly growth, and prioritized recommendations.',
  premium:
    'Everything in Standard plus Assessments 4–5 (Personality & AI), flexible cohorts, cluster insights, consulting-style plans, a dedicated contact, and marketing toolkit.',
};
